import {
  FIELD_CONFIG,
  type LayoutField,
} from "@/components/features/matter/file-repo/signature-requests/SignatureRequestPdfLayoutEditor.constants";
import { Button, Typography } from "@/components/ui";
import { PDFJsViewer } from "@/components/ui/PDFJsViewer/PDFJsViewer";
import { cn } from "@/lib/utils/cn";
import { getReusableSignerFieldValue } from "@/components/features/signature-request/reusableSignerFieldValue";
import { generateSignedSignatureRequestPdfBlob } from "@/components/features/signature-request/signatureRequestPdfSigning";
import { useSignerTemplateFields } from "./useSignerTemplateFields";
import { SignerFieldText } from "./signerFieldTextStyle";
import {
  CalendarDays,
  CheckSquare2,
  Download,
  PenLine,
  RotateCcw,
  Square,
  Type,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import {
  DemoSignatureFieldEditorDialog,
  type DemoSignedField,
} from "./DemoSignatureFieldEditorDialog";

type DemoSignatureRequestViewProps = {
  fileName: string;
  initialDocUrl: string;
  layoutFields: LayoutField[];
  onBack: () => void;
};

type DemoFieldValues = Record<string, string>;

function cloneDemoFields(fields: LayoutField[]) {
  return fields.map((field) => ({ ...field })) as DemoSignedField[];
}

function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  window.setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 0);
}

function isSignatureField(field: LayoutField) {
  return field.type === "signature" || field.type === "initials";
}

function isCheckboxField(field: LayoutField) {
  return field.type === "checkbox";
}

function getFieldDisplayLabel(field: LayoutField) {
  const label = field.label.trim();
  return label || FIELD_CONFIG[field.type].label;
}

function getFieldStatus(field: DemoSignedField, fieldValues: DemoFieldValues) {
  if (isSignatureField(field)) {
    return field.imageDataUrl ? "Signed" : "Open";
  }

  if (isCheckboxField(field)) {
    return field.checked ? "Checked" : "Open";
  }

  return fieldValues[field.id].trim() ? "Filled" : "Open";
}

function isFieldComplete(field: DemoSignedField, fieldValues: DemoFieldValues) {
  if (isSignatureField(field)) {
    return Boolean(field.imageDataUrl);
  }

  if (isCheckboxField(field)) {
    return Boolean(field.checked);
  }

  return Boolean(fieldValues[field.id].trim());
}

function toggleCheckboxField(
  fields: DemoSignedField[],
  fieldId: string,
): DemoSignedField[] {
  return fields.map((field) =>
    field.id === fieldId ? { ...field, checked: !field.checked } : field,
  );
}

function getReusableSignatureDataUrl(
  fields: DemoSignedField[],
  signerId: string,
  fieldType: "signature" | "initials",
) {
  return fields.find(
    (field) =>
      field.type === fieldType &&
      field.signerId === signerId &&
      Boolean(field.imageDataUrl),
  )?.imageDataUrl;
}

function sortFields(fields: DemoSignedField[]) {
  return [...fields].sort((left, right) => {
    const leftOrder = left.signerOrder ?? Number.POSITIVE_INFINITY;
    const rightOrder = right.signerOrder ?? Number.POSITIVE_INFINITY;
    if (leftOrder !== rightOrder) {
      return leftOrder - rightOrder;
    }
    if (left.page !== right.page) {
      return left.page - right.page;
    }
    if (left.y !== right.y) {
      return left.y - right.y;
    }
    if (left.x !== right.x) {
      return left.x - right.x;
    }
    return left.label.localeCompare(right.label);
  });
}

export function DemoSignatureRequestView({
  fileName,
  initialDocUrl,
  layoutFields,
  onBack,
}: DemoSignatureRequestViewProps) {
  const [fields, setFields] = useState<DemoSignedField[]>(() =>
    cloneDemoFields(layoutFields),
  );
  const [fieldValues, setFieldValues] = useState<DemoFieldValues>(() =>
    layoutFields.reduce<DemoFieldValues>((acc, field) => {
      if (!isSignatureField(field) && !isCheckboxField(field)) {
        acc[field.id] = "";
      }
      return acc;
    }, {}),
  );
  const [activeFieldId, setActiveFieldId] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  useSignerTemplateFields(initialDocUrl, setFields);

  const signedCount = useMemo(
    () => fields.filter((field) => isFieldComplete(field, fieldValues)).length,
    [fieldValues, fields],
  );
  const signedFieldCountLabel = `${signedCount}/${fields.length} complete`;

  const resetDemo = useCallback(() => {
    setFields(cloneDemoFields(layoutFields));
    setFieldValues(
      layoutFields.reduce<DemoFieldValues>((acc, field) => {
        if (!isSignatureField(field) && !isCheckboxField(field)) {
          acc[field.id] = "";
        }
        return acc;
      }, {}),
    );
    setActiveFieldId(null);
  }, [layoutFields]);

  const handleExport = useCallback(async () => {
    setIsExporting(true);
    try {
      const blob = await generateSignedSignatureRequestPdfBlob({
        fields,
        fieldValues,
        sourceUrl: initialDocUrl,
      });
      downloadBlob(blob, fileName);
    } finally {
      setIsExporting(false);
    }
  }, [fieldValues, fields, fileName, initialDocUrl]);

  return (
    <div className="dark:bg-background flex h-svh w-full flex-col bg-slate-50">
      <div className="dark:bg-card border-b bg-white px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <Typography variant="heading" size="sm">
              Demo signature portal
            </Typography>
            <Typography className="text-muted-foreground truncate text-sm">
              Click any field to edit it.
            </Typography>
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onBack}>
              Back
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={resetDemo}
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              Reset
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => {
                void handleExport();
              }}
              disabled={isExporting || signedCount === 0}
            >
              <Download className="mr-2 h-4 w-4" />
              {isExporting ? "Exporting..." : "Export signed PDF"}
            </Button>
          </div>
        </div>
      </div>

      <div className="flex min-h-0 flex-1">
        <div className="min-w-0 flex-1">
          <PDFJsViewer
            src={initialDocUrl}
            showToolbar
            defaultZoomMultiplier={0.9}
            className="h-full"
            contentClassName="p-4"
            renderPageOverlay={({ pageNumber, pageWidth, scale }) => (
              <div className="absolute inset-0">
                {sortFields(fields)
                  .filter((field) => field.page === pageNumber)
                  .map((field) => {
                    const value = fieldValues[field.id] ?? "";

                    if (isSignatureField(field)) {
                      const reusableSignatureDataUrl =
                        getReusableSignatureDataUrl(
                          fields,
                          field.signerId,
                          field.type === "signature" ? "signature" : "initials",
                        );
                      const hasSignatureData = Boolean(field.imageDataUrl);

                      return (
                        <button
                          key={field.id}
                          type="button"
                          className={cn(
                            "absolute overflow-hidden rounded-[4px] border px-2 py-1 text-left shadow-sm transition-colors",
                            "focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none",
                            hasSignatureData
                              ? "border-blue-500/80 bg-transparent hover:bg-transparent"
                              : "border-blue-500/80 bg-blue-500/10 hover:bg-blue-500/15",
                          )}
                          style={{
                            left: `${field.x * 100}%`,
                            top: `${field.y * 100}%`,
                            width: `${field.width * 100}%`,
                            height: `${field.height * 100}%`,
                          }}
                          onClick={() => {
                            if (
                              (field.type === "initials" ||
                                field.type === "signature") &&
                              !hasSignatureData &&
                              reusableSignatureDataUrl
                            ) {
                              setFields((current) =>
                                current.map((item) =>
                                  item.id === field.id
                                    ? {
                                        ...item,
                                        imageDataUrl: reusableSignatureDataUrl,
                                      }
                                    : item,
                                ),
                              );
                              return;
                            }

                            setActiveFieldId(field.id);
                          }}
                        >
                          {field.imageDataUrl ? (
                            <img
                              src={field.imageDataUrl}
                              alt=""
                              className="h-full w-full object-fill"
                              draggable={false}
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center gap-1.5 text-[11px] font-semibold">
                              <PenLine className="h-3.5 w-3.5" />
                              {getFieldDisplayLabel(field)}
                            </div>
                          )}
                        </button>
                      );
                    }

                    if (isCheckboxField(field)) {
                      return (
                        <button
                          key={field.id}
                          type="button"
                          className={cn(
                            "absolute overflow-hidden rounded-[4px] border px-2 py-1 text-left shadow-sm transition-colors",
                            field.checked
                              ? "border-amber-500/70 bg-transparent hover:bg-transparent"
                              : "border-amber-500/70 bg-amber-500/10 hover:bg-amber-500/15",
                          )}
                          style={{
                            left: `${field.x * 100}%`,
                            top: `${field.y * 100}%`,
                            width: `${field.width * 100}%`,
                            height: `${field.height * 100}%`,
                          }}
                          onClick={() => {
                            setFields((current) =>
                              toggleCheckboxField(current, field.id),
                            );
                          }}
                        >
                          <div className="flex h-full w-full items-center gap-1.5 px-2 py-1">
                            {field.checked ? (
                              <CheckSquare2 className="h-3.5 w-3.5 shrink-0 text-amber-700" />
                            ) : (
                              <Square className="h-3.5 w-3.5 shrink-0 text-amber-700" />
                            )}
                            <span className="min-w-0 flex-1 truncate text-[11px] font-medium text-amber-950">
                              {field.checked ? "Checked" : "Unchecked"}
                            </span>
                          </div>
                        </button>
                      );
                    }

                    return (
                      <button
                        key={field.id}
                        type="button"
                        className={cn(
                          "absolute overflow-hidden rounded-[4px] border shadow-sm transition-colors",
                          value.trim()
                            ? "border-emerald-500/70 bg-transparent hover:bg-transparent"
                            : "border-emerald-500/70 bg-emerald-500/10 hover:bg-emerald-500/15",
                        )}
                        style={{
                          left: `${field.x * 100}%`,
                          top: `${field.y * 100}%`,
                          width: `${field.width * 100}%`,
                          height: `${field.height * 100}%`,
                        }}
                        onClick={() => {
                          if (!isFieldComplete(field, fieldValues)) {
                            const reusableValue = getReusableSignerFieldValue(
                              fields,
                              fieldValues,
                              field,
                            );
                            if (reusableValue) {
                              setFieldValues((current) => ({
                                ...current,
                                [field.id]: reusableValue,
                              }));
                              return;
                            }
                          }
                          setActiveFieldId(field.id);
                        }}
                      >
                        {value ? (
                          <SignerFieldText
                            field={field}
                            value={value}
                            scale={scale}
                            pageWidth={pageWidth}
                          />
                        ) : (
                          <div className="flex h-full w-full items-center gap-1.5 px-2 py-1">
                            <Type className="h-3.5 w-3.5 shrink-0 text-emerald-700" />
                            <span className="min-w-0 flex-1 truncate text-[11px] font-medium text-emerald-950">
                              {value || getFieldDisplayLabel(field)}
                            </span>
                          </div>
                        )}
                      </button>
                    );
                  })}
              </div>
            )}
          />
        </div>

        <aside className="dark:bg-card w-80 shrink-0 border-l bg-white p-4">
          <div className="space-y-4">
            <div>
              <Typography variant="heading" size="sm">
                Fields
              </Typography>
              <Typography className="text-muted-foreground text-sm">
                {signedFieldCountLabel}
              </Typography>
            </div>

            <div className="space-y-2">
              {fields.map((field) => {
                const isSigned = isFieldComplete(field, fieldValues);
                const textField =
                  !isSignatureField(field) && !isCheckboxField(field);
                return (
                  <button
                    key={field.id}
                    type="button"
                    className={cn(
                      "flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm transition-colors",
                      textField
                        ? "border-emerald-200 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/30"
                        : isSigned
                          ? "border-emerald-200 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/30"
                          : "dark:bg-card bg-white",
                    )}
                    onClick={() => {
                      if (isCheckboxField(field)) {
                        setFields((current) =>
                          toggleCheckboxField(current, field.id),
                        );
                        return;
                      }

                      setActiveFieldId(field.id);
                    }}
                  >
                    <span className="flex min-w-0 items-center gap-2 truncate">
                      {textField ? (
                        field.type === "date" ? (
                          <CalendarDays className="h-4 w-4 shrink-0 text-emerald-600" />
                        ) : (
                          <Type className="h-4 w-4 shrink-0 text-emerald-600" />
                        )
                      ) : isCheckboxField(field) ? (
                        field.checked ? (
                          <CheckSquare2 className="h-4 w-4 shrink-0 text-amber-600" />
                        ) : (
                          <Square className="h-4 w-4 shrink-0 text-amber-600" />
                        )
                      ) : (
                        <PenLine className="h-4 w-4 shrink-0 text-blue-500" />
                      )}
                      {getFieldDisplayLabel(field)}
                    </span>
                    <span className="text-muted-foreground text-xs">
                      {getFieldStatus(field, fieldValues)}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </aside>
      </div>

      <DemoSignatureFieldEditorDialog
        activeFieldId={activeFieldId}
        fieldValues={fieldValues}
        fields={fields}
        onClose={() => {
          setActiveFieldId(null);
        }}
        setFieldValues={setFieldValues}
        setFields={setFields}
      />
    </div>
  );
}
